import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type WorkspaceRole = 'WORKSPACE_ADMIN' | 'OPERADOR' | 'CONTADOR_EXTERNO';

export interface WorkspaceUser {
  id: string;
  email: string;
  name: string;
  role: WorkspaceRole;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserInput {
  email: string;
  name: string;
  role: WorkspaceRole;
}

export interface CreateUserResult {
  user: Omit<WorkspaceUser, 'updatedAt'>;
  tempPassword: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/users`;
}

export function useWorkspaceUsers(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['workspace-users', workspaceId],
    queryFn: () => api.get<{ items: WorkspaceUser[] }>(`${basePath(workspaceId!)}/`),
  });
}

export function useCreateWorkspaceUser(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api.post<CreateUserResult>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
    },
  });
}

export function useUpdateWorkspaceUser(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; name?: string; role?: WorkspaceRole }) =>
      api.patch<WorkspaceUser>(`${basePath(workspaceId!)}/${vars.id}`, {
        name: vars.name,
        role: vars.role,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
    },
  });
}

export function useDeleteWorkspaceUser(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
    },
  });
}
